import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { contactAPI } from '../services/api';
import ReCAPTCHA from 'react-google-recaptcha';
import Navbar from '../components/Navbar';
import './LandingPage.css';
import './DMIPSection.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  const handleStartDiscovery = () => {
    navigate('/dmip');
  };

  const handleContactFormChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ loading: true, success: false, error: null });

    try {
      // Get reCAPTCHA token
      const recaptchaToken = recaptchaRef.current ? await recaptchaRef.current.executeAsync() : null;

      if (!recaptchaToken) {
        setFormStatus({
          loading: false,
          success: false,
          error: 'Please complete the reCAPTCHA verification.'
        });
        return;
      }

      const response = await contactAPI.submitContactForm({
        ...contactForm,
        recaptchaToken
      });

      setFormStatus({
        loading: false,
        success: true,
        error: null
      });

      // Reset form and reCAPTCHA
      setContactForm({
        name: '',
        email: '',
        message: ''
      });
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      // Clear success message after 5 seconds
      setTimeout(() => {
        setFormStatus(prev => ({ ...prev, success: false }));
      }, 5000);

    } catch (error) {
      setFormStatus({
        loading: false,
        success: false,
        error: error.response?.data?.message || 'Failed to send message. Please try again.'
      });

      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      // Clear error message after 5 seconds
      setTimeout(() => {
        setFormStatus(prev => ({ ...prev, error: null }));
      }, 5000);
    }
  };

  return (
    <div className="landing-page">
      <Navbar title="Data Migration Framework" />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">Data Migration Framework</h1>
              <h2 className="hero-subtitle">Removing the Tech Debt</h2>
              <p className="hero-description">
                Every business application today will eventually become a legacy system.
                Will you be prepared when that time comes? Navigate the high-stakes challenge
                of legacy system decommissioning with a proven framework.
              </p>
              <div className="hero-cta">
                <p className="hero-eyebrow">The Framework is Now a Platform.</p>
                <button onClick={handleStartDiscovery} className="btn btn-primary btn-lg">
                  Start Your Project Discovery
                </button>
              </div>
            </div>
            <div className="hero-image">
              <img src="/book.png" alt="Data Migration Framework Book" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header text-center">
            <h2>What Readers Are Saying</h2>
            <p>
              Trusted by project and data management professionals.
            </p>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-quote">
                <p>
                  "Practical! An excellent read for any one leading a data migration initiative.
                  As a delivery manager myself, this book provides step by step guide with the
                  risks associated for any data migration. Pure Gold."
                </p>
              </div>
              <div className="testimonial-author">
                <h4>Melissa Tsalicoglou</h4>
                <p>Program Manager</p>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-quote">
                <p>
                  "Not only is it refreshingly honest ("Every business application today will
                  eventually become a legacy system") - it's also finally an actionable framework
                  that can help prevent making the most common mistakes when migrating data."
                </p>
              </div>
              <div className="testimonial-author">
                <h4>Tiankai Feng</h4>
                <p>Director, Data & AI Strategy</p>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-quote">
                <p>
                  "I enjoyed reading the book very much. I was a data warehouse developer for
                  four years and I wish I had your book back then!"
                </p>
              </div>
              <div className="testimonial-author">
                <h4>Steve Hoberman, DMC</h4>
                <p>Editor-in-chief Technics Publications, Lecturer at Columbia University, Data Modeler "OG"</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Framework Section */}
      <section className="framework-section">
        <div className="container">
          <div className="section-header text-center">
            <h2>The Data Migration Framework</h2>
            <p>
              A comprehensive, vendor-agnostic framework to guide business executives and
              technical leaders through every stage of legacy system decommissioning.
            </p>
          </div>
          <div className="framework-content">
            <div className="framework-image">
              <img src="/DM-DMF.png" alt="Data Migration Framework Diagram" />
            </div>
            <div className="framework-features">
              <div className="feature-card">
                <h3>10 Key Components</h3>
                <ul>
                  <li>Scope - Define migration boundaries</li>
                  <li>Segment - Logical data segments and applied compliances</li>
                  <li>People - Resources and tools</li>
                  <li>Business Rules - Validate mappings</li>
                  <li>Data Quality - Ensure fitness</li>
                  <li>Integration - Manage dependencies</li>
                  <li>Goals - Track progress</li>
                  <li>Testing - Verify migration</li>
                  <li>Go-Live - Execute cutover</li>
                  <li>Post Migration - Validate success</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DMIP Section */}
      <section className="dmip-section">
        <div className="container">
          <div className="section-header text-center">
            <h2>The Framework is Now a Platform.</h2>
          </div>
          <p className="dmip-subheading">
            Structured discovery. Knowledge graphs. AI-generated plans.
            Built on D-DMF. Running on real engagements.
          </p>

          <div className="dmip-cards">
            {/* Card 1 */}
            <div className="dmip-card">
              <div className="dmip-card-icon">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="6" cy="12" r="2"/><circle cx="18" cy="6" r="2"/><circle cx="18" cy="18" r="2"/>
                  <path strokeLinecap="round" d="M8 11.5l8-4M8 12.5l8 4"/>
                </svg>
              </div>
              <h3>Structured Discovery</h3>
              <p>
                10-component D-DMF engine captures every legacy system — scope,
                segments, risks, integrations, data quality, and governance — in one
                structured engagement.
              </p>
            </div>

            {/* Card 2 */}
            <div className="dmip-card">
              <div className="dmip-card-icon">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 3l4 4-4 4M12 3l4 4-4 4M19 12c0 3.866-3.134 7-7 7s-7-3.134-7-7"/>
                </svg>
              </div>
              <h3>Knowledge Graph</h3>
              <p>
                Every answer builds a live knowledge graph connecting systems,
                people, risks, and integrations. Cross-system intelligence surfaces
                automatically.
              </p>
            </div>

            {/* Card 3 */}
            <div className="dmip-card">
              <div className="dmip-card-icon">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <h3>AI Plan Generation</h3>
              <p>
                Migration Plans, Governance Plans, and Data Quality Plans
                generated from discovery data. Section by section. Export to Word.
              </p>
            </div>
          </div>

          <div className="dmip-cta">
            <a href="mailto:krupesh@dmip.app?subject=DMIP Access Request" className="btn-primary">Request Access</a>
            <a href="mailto:krupesh@dmip.app?subject=DMIP Partnership Enquiry" className="btn-secondary">Partner With Us</a>
          </div>
        </div>
      </section>

      {/* Book Purchase Section */}
      <section className="book-section">
        <div className="container">
          <div className="book-content">
            <div className="book-image">
              <img src="/Cover.png" alt="Book Cover" />
            </div>
            <div className="book-info">
              <h2>Get the Book</h2>
              <p className="book-description">
                This book is your guide to navigating the high-stakes challenge of legacy system
                decommissioning without falling into the common pitfalls that lead to project failure.
              </p>
              <p className="book-offer">
                You can explore Krupesh's book, Data Migration Framework,{' '}
                <a
                  href="https://technicspub.com/data-migration-framework/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="book-link"
                >
                  here
                </a>{' '}
                and use code <strong>TP25</strong> for a 25% discount. It's also available
                globally on{' '}
                <a
                  href="https://www.amazon.com/Data-Migration-Framework-Removing-Tech/dp/1634627431"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="book-link"
                >
                  Amazon
                </a>.
              </p>
              <div className="book-cta">
                <a
                  href="https://technicspub.com/data-migration-framework/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary btn-lg"
                >
                  Buy Book (25% Off)
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Section */}
      <section className="course-section">
        <div className="container">
          <div className="final-cta-content text-center">
            <h2>Learn Through The Course</h2>
            <p>
              Take your data migration expertise to the next level with a comprehensive course
              by Krupesh Desai that walks you through implementing the framework in your organization.
            </p>

            {/* YouTube Video Grid */}
            <div className="videos-grid">
              <div className="video-item">
                <div className="video-container">
                  <iframe
                    src="https://www.youtube.com/embed/CT3zi43e4gA"
                    title="Value Added Data Migration Course Introduction"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <h3>Course Introduction - Value Added Data Migration</h3>
              </div>
              <div className="video-item">
                <div className="video-container">
                  <iframe
                    src="https://www.youtube.com/embed/dkht8UxXgC0"
                    title="Data Migration - The Problem"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <h3>Data Migration - The Problem</h3>
              </div>
            </div>

            <a
              href="https://academy.hopp.tech/products/courses/value-added-data-migration"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary btn-lg"
            >
              Enroll in Course
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="container">
          <div className="final-cta-content text-center">
            <h2>Ready to Create Your Data Migration Plan?</h2>
            <p>
              Use our interactive framework to conduct project discovery and develop a strategic data migration plan
              tailored to your legacy system decommissioning project.
            </p>

            {/* YouTube Videos Grid */}
            <div className="videos-grid">
              <div className="video-item">
                <div className="video-container">
                  <iframe
                    src="https://www.youtube.com/embed/pLt8CJU_vKE"
                    title="Audit and Compliance in Data Migration"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <h3>Audit and Compliance in Data Migration</h3>
              </div>
              <div className="video-item">
                <div className="video-container">
                  <iframe
                    src="https://www.youtube.com/embed/dhD-vgueD0U"
                    title="Data Migration Plan - The Anchor"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <h3>Data Migration Plan - The Anchor</h3>
              </div>
            </div>

            <button onClick={handleStartDiscovery} className="btn btn-primary btn-lg">
              Create Your Data Migration Plan
            </button>
          </div>
        </div>
      </section>

      {/* About Author Section */}
      <section className="author-section">
        <div className="container">
          <div className="section-header text-center">
            <h2>About the Author</h2>
          </div>
          <div className="author-content">
            <div className="author-bio">
              <h3>
                Krupesh Desai
                <a
                  href="https://www.linkedin.com/in/krupeshdesai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="linkedin-icon"
                  aria-label="LinkedIn Profile"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </h3>
              <p className="author-title">Simplifying the language of data.</p>
              <p>
							Krupesh Desai is a Certified Data Management Professional (CDMP) with over a decade of experience solving complex data challenges across industries. Starting as a software engineer in database recovery and replication, he has evolved through leadership roles in data data migration, integration, and governance.
              </p>
              <p>
							Krupesh has successfully led multiple legacy decommissioning projects, delivering robust migration and analytics solutions with seamless business continuity. A lifelong learner, Krupesh focuses on simplifying data strategy for decision-makers through practical research and writing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section - TEMPORARILY DISABLED */}
      {/* Uncomment the section below to re-enable the contact form */}
      {/*
      <section className="contact-section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Need Implementation Support?</h2>
          </div>
          <div className="contact-content">
            <form className="contact-form" onSubmit={handleContactFormSubmit}>
              {formStatus.success && (
                <div className="form-success">
                  Thank you! Your message has been sent successfully. We'll get back to you soon!
                </div>
              )}
              {formStatus.error && (
                <div className="form-error">
                  {formStatus.error}
                </div>
              )}
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  placeholder="Your name"
                  value={contactForm.name}
                  onChange={handleContactFormChange}
                  disabled={formStatus.loading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  placeholder="your.email@example.com"
                  value={contactForm.email}
                  onChange={handleContactFormChange}
                  disabled={formStatus.loading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  required
                  placeholder="Tell us about your project and how we can help..."
                  value={contactForm.message}
                  onChange={handleContactFormChange}
                  disabled={formStatus.loading}
                ></textarea>
              </div>

              <div className="recaptcha-container">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'}
                  size="invisible"
                />
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={formStatus.loading}
                >
                  {formStatus.loading ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
      */}

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content text-center">
            <p>&copy; {new Date().getFullYear()} Data Migration Framework. All rights reserved.</p>
            <p>By Krupesh Desai</p>
            <div className="footer-links">
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
